/* A small picture of a sheet (sidebar, review): the film, and each copy drawn
   from its thumbnail at its place, size, rotation and flip. */

import { useEffect, useReducer, useRef } from 'react';
import type { Sheet } from '@gsb/core';
import { useStore } from '../store/store';
import { peekImage } from './images';

export function SheetThumb({ sheet, width, maxHeight = 200 }: { sheet: Sheet; width: number; maxHeight?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const designs = useStore(s => s.doc.designs);
  const assets = useStore(s => s.assets);
  const [n, redraw] = useReducer((x: number) => x + 1, 0);
  let s = width / sheet.widthMm;
  if (sheet.lengthMm * s > maxHeight) s = maxHeight / sheet.lengthMm;
  const W = Math.max(8, Math.round(sheet.widthMm * s)), H = Math.max(8, Math.round(sheet.lengthMm * s));
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = W * dpr; c.height = H * dpr;
    const x = c.getContext('2d')!;
    x.scale(dpr, dpr);
    // film: a fine checkerboard
    const cell = Math.max(3, Math.round(W / 14));
    x.fillStyle = '#FFFFFF'; x.fillRect(0, 0, W, H);
    x.fillStyle = '#EAF0F3';
    for (let yy = 0; yy < H; yy += cell) for (let xx = (yy / cell) % 2 ? cell : 0; xx < W; xx += cell * 2) x.fillRect(xx, yy, cell, cell);
    const byId = new Map(designs.map(d => [d.id, d]));
    for (const it of sheet.items) {
      const d = byId.get(it.designId);
      const a = d ? assets[d.assetId] : undefined;
      const img = peekImage(a?.thumbUrl, redraw);
      const w = it.widthMm * s, h = it.heightMm * s;
      x.save();
      x.translate(it.cx * s, it.cy * s);
      x.rotate((it.rotation * Math.PI) / 180);
      x.scale(it.flipX ? -1 : 1, it.flipY ? -1 : 1);
      if (img && a) {
        const t = d && !d.trim ? a.trim : { x: 0, y: 0, w: 1, h: 1 };
        x.drawImage(img, -w / 2 + t.x * w, -h / 2 + t.y * h, t.w * w, t.h * h);
      } else {
        x.fillStyle = 'rgba(17,197,217,.45)';
        x.fillRect(-w / 2, -h / 2, w, h);
      }
      x.restore();
    }
  }, [sheet, designs, assets, W, H, n]);
  return <canvas ref={ref} className="sthumb" style={{ width: W, height: H }} aria-hidden="true" />;
}
